import { useLocation } from 'react-router-dom'

import getPhoenixUrlParams from '../lib/get-phoenix-url-params'
import BookableItem, { AssignedSpot, EventBookableItem } from '../typings/BookableItem'
import Invoice from '../typings/Invoice'
import { SettleWith, ShopProduct } from '../typings/Product'
import Trainer from '../typings/Trainer'
import Venue from '../typings/Venue'

import {
  EventType,
  UseBookingDetailsCartDocument,
  UseBookingDetailsCartQuery,
  UseBookingDetailsOrderDocument,
  UseBookingDetailsOrderQuery,
} from '../graphql'
import useCart from './useCart'
import useOrder from './useOrder'

type BookingDetails = InvoiceFlow | ProductFlow | BookableItemFlow | PrivateSessionFlow | UnsupportedFlow

interface InvoiceFlow {
  type: 'invoice'
  invoice: Invoice
  venue: Venue
}

interface ProductFlow {
  type: 'product'
  product: ShopProduct
  venue: Venue
}

interface BookableItemFlow {
  type: 'bookableItem'
  bookableItem: BookableItem
  settleWith?: SettleWith | null
  hasSpotBooking: boolean
  assignedSpot?: AssignedSpot
  description?: {
    plainText: string
  } | null
}

interface UnsupportedFlow {
  type: 'unsupported'
}

interface PrivateSessionFlow {
  type: 'privateSession'
  settleWith?: SettleWith | null
  bookableItem: EventBookableItem
  trainer: Trainer | null
  start: string | null
  end: string | null
}

interface Result {
  bookingDetails?: BookingDetails
  isBookingDetailsLoading: boolean
}

const useBookingDetails = (): Result => {
  const location = useLocation()
  const { cartId, orderId } = getPhoenixUrlParams(location.search)

  const { cartData, isCartLoading } = useCart<UseBookingDetailsCartQuery>(UseBookingDetailsCartDocument, {
    skip: !cartId,
  })

  const { orderData, isOrderLoading } = useOrder<UseBookingDetailsOrderQuery>(UseBookingDetailsOrderDocument, {
    skip: !orderId,
  })

  const bookingDetailsData = (cartData && cartData.cart) || (orderData && orderData.order)
  const isBookingDetailsLoading = isCartLoading || isOrderLoading

  const bookingDetails = getBookingDetails(bookingDetailsData)

  return {
    bookingDetails,
    isBookingDetailsLoading,
  }
}

const getBookingDetails = (
  bookingDetailsData?: UseBookingDetailsCartQuery['cart'] | UseBookingDetailsOrderQuery['order'],
): BookingDetails | undefined => {
  if (!bookingDetailsData) return undefined

  const bookableItemCartItem =
    bookingDetailsData.__typename === 'Cart' &&
    bookingDetailsData.items.find((item) => item.__typename === 'BookableItemCartItem')
  const bookingOrderItem =
    bookingDetailsData.__typename === 'Order' &&
    bookingDetailsData.items.find((item) => item.__typename === 'BookingOrderItem')
  const bookingRequestOrderItem =
    bookingDetailsData.__typename === 'Order' &&
    bookingDetailsData.items.find((item) => item.__typename === 'BookingRequestOrderItem')
  const productCartItem =
    bookingDetailsData.__typename === 'Cart' &&
    bookingDetailsData.items.find((item) => item.__typename === 'ProductCartItem')

  const hasMultipleItems = bookingDetailsData && bookingDetailsData.items.length > 1
  const hasGenericAssignedProduct =
    bookingDetailsData.__typename === 'Order' &&
    bookingDetailsData.items.find(
      (item) =>
        item.__typename === 'AssignedProductOrderItem' && item.assignedProduct.__typename === 'GenericAssignedProduct',
    )
  const wasSomethingPaidWithAProduct =
    bookingDetailsData.__typename === 'Order' && bookingDetailsData.items.some((item) => item.confirmedWith)

  if (
    bookingDetailsData.__typename === 'Order' &&
    bookingDetailsData.invoice &&
    bookingDetailsData.invoice.number &&
    hasMultipleItems &&
    !wasSomethingPaidWithAProduct
  ) {
    return {
      type: 'invoice',
      venue: bookingDetailsData.venue,
      invoice: bookingDetailsData.invoice,
    }
  }

  // TODO: Adapt this condition when we actually also fetch information
  // regarding the court bookable items and court bookings as then we don't need
  // to check for the bookable item type specifically for events only
  const eventBookableItem =
    (bookableItemCartItem &&
      bookableItemCartItem.__typename === 'BookableItemCartItem' &&
      bookableItemCartItem.bookableItem.__typename === 'EventBookableItem' &&
      bookableItemCartItem.bookableItem) ||
    (bookingOrderItem &&
      bookingOrderItem.__typename === 'BookingOrderItem' &&
      bookingOrderItem.booking.__typename === 'EventBooking' &&
      bookingOrderItem.booking.bookableItem)

  const settleWith =
    (bookableItemCartItem &&
      bookableItemCartItem.__typename === 'BookableItemCartItem' &&
      bookableItemCartItem.settleWith) ||
    (bookingOrderItem &&
      bookingOrderItem.__typename === 'BookingOrderItem' &&
      bookingOrderItem.booking.__typename === 'EventBooking' &&
      (bookingOrderItem.confirmedWith || bookingOrderItem.purchasedWith)) ||
    (bookingRequestOrderItem &&
      bookingRequestOrderItem.__typename === 'BookingRequestOrderItem' &&
      bookingRequestOrderItem.confirmedWith) ||
    null

  if (
    bookingDetailsData.__typename === 'Cart' &&
    bookableItemCartItem &&
    bookableItemCartItem.__typename === 'BookableItemCartItem' &&
    bookableItemCartItem.bookableItem.__typename === 'EventBookableItem' &&
    bookableItemCartItem.bookableItem.eventType === EventType.PRIVATE
  ) {
    const start = bookableItemCartItem.timeRange ? bookableItemCartItem.timeRange.start : null
    const end = bookableItemCartItem.timeRange ? bookableItemCartItem.timeRange.end : null

    return {
      type: 'privateSession',
      settleWith,
      bookableItem: bookableItemCartItem.bookableItem,
      trainer: bookableItemCartItem.trainer ?? null,
      start,
      end,
    }
  }

  // Court rentals have no event sessions before booking, the booked slot is only available on the cart item
  if (
    bookingDetailsData.__typename === 'Cart' &&
    bookableItemCartItem &&
    bookableItemCartItem.__typename === 'BookableItemCartItem' &&
    bookableItemCartItem.bookableItem.__typename === 'EventBookableItem' &&
    bookableItemCartItem.bookableItem.eventType === EventType.RENTAL
  ) {
    const start = bookableItemCartItem.timeRange ? bookableItemCartItem.timeRange.start : null
    const end = bookableItemCartItem.timeRange ? bookableItemCartItem.timeRange.end : null

    return {
      // TODO: create new type for FE to handle, for now I'm using privateSession
      type: 'privateSession',
      settleWith,
      bookableItem: bookableItemCartItem.bookableItem,
      trainer: null,
      start,
      end,
    }
  }

  if (eventBookableItem) {
    const assignedSpot =
      (bookingOrderItem &&
        bookingOrderItem.__typename === 'BookingOrderItem' &&
        bookingOrderItem.booking.__typename === 'EventBooking' &&
        bookingOrderItem.booking.assignedSpot) ||
      undefined

    return {
      type: 'bookableItem',
      bookableItem: eventBookableItem,
      settleWith,
      hasSpotBooking: Boolean(eventBookableItem && Boolean(eventBookableItem?.floorPlan?.id)),
      assignedSpot,
    }
  }

  if (bookingRequestOrderItem && bookingRequestOrderItem.__typename === 'BookingRequestOrderItem') {
    const start = bookingRequestOrderItem.timeRange ? bookingRequestOrderItem.timeRange.start : null
    const end = bookingRequestOrderItem.timeRange ? bookingRequestOrderItem.timeRange.end : null
    return {
      type: 'privateSession',
      settleWith,
      bookableItem: bookingRequestOrderItem.bookableItem,
      trainer: bookingRequestOrderItem.trainer ?? null,
      start,
      end,
    }
  }

  // Special cases that are only existing here because we currently don't support them
  // They will eventually be handled by the normal 'event' / 'product' flows
  //
  // 1 - Court flow (currently the only way to go to the new checkout with an court is selling one through an invoice)
  // 2 - Generic assigned products (generic template for assigned products that are not implemented yet)
  if (bookableItemCartItem || bookingOrderItem || hasGenericAssignedProduct) {
    return {
      type: 'invoice',
      venue: bookingDetailsData.venue,
      // @ts-ignore This types don't have an invoice, but we can only get here if we are on an invoice flow
      invoice: bookingDetailsData.invoice,
    }
  }

  if (productCartItem && productCartItem.__typename === 'ProductCartItem') {
    return {
      type: 'product',
      venue: bookingDetailsData.venue,
      product: productCartItem.product,
    }
  }

  if (
    bookingDetailsData.__typename === 'Order' &&
    !bookingDetailsData.items.some(
      (item) => item.__typename === 'BookingOrderItem' || item.__typename === 'BookingRequestOrderItem',
    )
  ) {
    const product =
      bookingDetailsData.items.find((item) => item.__typename === 'AssignedProductOrderItem' && item.confirmedWith) ||
      bookingDetailsData.items.find((item) => item.__typename === 'AssignedProductOrderItem' && item.purchasedWith)

    return {
      type: 'product',
      venue: bookingDetailsData.venue,
      // @ts-ignore Don't know why TS doesn't understand that its not possible for
      // the assigned product to be a generic assigned product
      product: product.assignedProduct.product,
    }
  }

  // Throwable error cases (we want to handle this on the layout level so error boundaries can catch an error)
  if (
    bookingDetailsData.__typename === 'Cart' ||
    (bookingDetailsData.__typename === 'Order' && !bookingDetailsData.invoice)
  ) {
    return {
      type: 'unsupported',
    }
  }

  // Default if we don't manage to do any matching like an unknown item type coming from the invoice
  return {
    type: 'invoice',
    venue: bookingDetailsData.venue,
    invoice: bookingDetailsData.invoice!,
  }
}

export default useBookingDetails
